import { Check, ChevronDown, Copy, Download, Plus, Search, Share2, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
    getActiveBanking,
    getAllBanking,
    createBanking,
    updateBanking,
    toggleBankingActive,
    deleteBanking,
} from '../../../api/banking.api';
import styles from '../../../assets/scss/pages/PointRuleList.module.scss';
import { useAuthStore } from '../../../stores/authStore';
import FormattedInput from '../../../components/common/FormattedInput';
import axios from 'axios';

const toUpper = (v) => String(v ?? '').toUpperCase();

const BANKS = [
    { bin: '970422', name: 'MB Bank' },
    { bin: '970436', name: 'Vietcombank' },
    { bin: '970407', name: 'Techcombank' },
    { bin: '970432', name: 'VPBank' },
    { bin: '970405', name: 'Agribank' },
    { bin: '970418', name: 'BIDV' },
    { bin: '970415', name: 'VietinBank' },
];

export default function BankingPage() {
    const user = useAuthStore((s) => s.user);
    const shopId = user?.shopId;

    const [activeBanking, setActiveBanking] = useState(null);
    const [bankingList, setBankingList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [toastMsg, setToastMsg] = useState(null);
    const [copied, setCopied] = useState('');

    // Panel form
    const [showPanel, setShowPanel] = useState(false);
    const [panelMode, setPanelMode] = useState('create'); // 'create' | 'edit'
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({ bankBin: '', bankName: '', accountNumber: '', accountName: '' });
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [showBankDropdown, setShowBankDropdown] = useState(false);
    const [bankSearch, setBankSearch] = useState('');

    // QR
    const [qrUrl, setQrUrl] = useState('');
    const [generatingQr, setGeneratingQr] = useState(false);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const handleCopy = (text, label) => {
        navigator.clipboard.writeText(text);
        setCopied(label);
        setTimeout(() => setCopied(''), 1500);
    };

    // === Fetch ===
    const fetchData = useCallback(async () => {
        if (!shopId) return;
        setLoading(true);
        try {
            const [activeRes, allRes] = await Promise.all([
                getActiveBanking(shopId),
                getAllBanking(shopId),
            ]);
            const active = activeRes.data?.data?.[0] || activeRes.data?.data || null;
            setActiveBanking(active);
            setBankingList(allRes.data?.data || []);
            if (active?.qrCodeUrl) setQrUrl(active.qrCodeUrl);
            else if (active) generateQr(active);
        } catch {
            setActiveBanking(null);
            setBankingList([]);
        } finally { setLoading(false); }
    }, [shopId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchData(); }, [fetchData]);

    // === QR generation ===
    const generateQr = async (info) => {
        if (!info?.bankBin || !info?.accountNumber || !info?.accountName) return;
        setGeneratingQr(true);
        try {
            const res = await axios.post('https://api.vietqr.io/v2/generate', {
                accountNo: info.accountNumber,
                accountName: info.accountName,
                acqId: info.bankBin,
                amount: 0,
                addInfo: 'Thanh toan',
                format: 'text',
                template: 'qr_only',
            });
            if (res.data?.code === '00' && res.data?.data?.qrDataURL) {
                const url = res.data.data.qrDataURL;
                setQrUrl(url);
                if (info.id) await updateBanking(info.id, { qrCodeUrl: url });
            }
        } catch { /* ignore */ }
        finally { setGeneratingQr(false); }
    };

    // === Panel ===
    const openCreate = () => {
        setPanelMode('create');
        setEditId(null);
        setForm({ bankBin: '', bankName: '', accountNumber: '', accountName: '' });
        setFormError('');
        setQrUrl('');
        setShowPanel(true);
    };

    const openEdit = (item) => {
        setPanelMode('edit');
        setEditId(item.id);
        setForm({
            bankBin: item.bankBin || '',
            bankName: item.bankName || '',
            accountNumber: item.accountNumber || '',
            accountName: item.accountName || '',
        });
        setFormError('');
        setQrUrl(item.qrCodeUrl || '');
        setShowPanel(true);
    };

    const closePanel = () => { setShowPanel(false); setFormError(''); setShowBankDropdown(false); };

    const selectBank = (bank) => {
        setForm((p) => ({ ...p, bankBin: bank.bin, bankName: bank.name }));
        setShowBankDropdown(false);
        setBankSearch('');
    };

    // Validate account name: uppercase, no diacritics
    const validateAccountName = (name) => /^[A-Z\s]+$/.test(name.trim());

    const handleSave = async () => {
        if (!form.bankBin || !form.accountNumber.trim() || !form.accountName.trim()) {
            setFormError('Vui lòng nhập đầy đủ thông tin');
            return;
        }
        if (!validateAccountName(form.accountName)) {
            setFormError('Tên tài khoản phải viết IN HOA, không dấu. VD: NGUYEN VAN A');
            return;
        }
        setSaving(true);
        setFormError('');
        try {
            const payload = {
                bankName: form.bankName,
                bankBin: form.bankBin,
                accountNumber: form.accountNumber.trim(),
                accountName: form.accountName.trim(),
                isActive: true,
            };
            let savedId;
            if (panelMode === 'create') {
                const res = await createBanking(payload);
                savedId = res.data?.data?.id || res.data?.id;
                showToast('success', 'Tạo tài khoản thành công');
            } else {
                await updateBanking(editId, payload);
                savedId = editId;
                showToast('success', 'Cập nhật thành công');
            }
            // Generate QR
            if (savedId) {
                await generateQr({ ...payload, id: savedId });
            }
            closePanel();
            fetchData();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Thao tác thất bại');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Xoá tài khoản ngân hàng này?')) return;
        try {
            await deleteBanking(id);
            showToast('success', 'Xoá thành công');
            if (editId === id) closePanel();
            fetchData();
        } catch { showToast('error', 'Xoá thất bại', 2500); }
    };

    const handleToggleActive = async (id) => {
        try {
            await toggleBankingActive(id);
            showToast('success', 'Đã chuyển tài khoản');
            fetchData();
        } catch { showToast('error', 'Thất bại', 2500); }
    };

    const filteredBanks = BANKS.filter((b) =>
        !bankSearch || b.name.toLowerCase().includes(bankSearch.toLowerCase()),
    );

    // Dynamic QR fallback
    const displayQrUrl = qrUrl || (activeBanking
        ? `https://img.vietqr.io/image/${activeBanking.bankBin}-${activeBanking.accountNumber}-qr_only.png`
        : '');

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Tài khoản ngân hàng</h1>
                    <p className={styles.subtitle}>Quản lý tài khoản nhận thanh toán</p>
                </div>
                <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={openCreate}>
                    <Plus /> Thêm tài khoản
                </button>
            </div>

            <div className={styles.layout}>
                {/* LEFT */}
                <div className={styles.layoutLeft}>
                    {/* Active QR display */}
                    {!loading && activeBanking && (
                        <div style={{
                            background: '#fff', borderRadius: 16, padding: 28,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.04), 0 0 0 1px rgba(0,0,0,0.03)',
                            display: 'flex', gap: 28, alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap',
                        }}>
                            {/* QR */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                                {displayQrUrl ? (
                                    <div style={{ border: '2px solid #0098F6', borderRadius: 12, padding: 10 }}>
                                        <img src={displayQrUrl} alt="QR" style={{ width: 180, height: 180, display: 'block' }} />
                                    </div>
                                ) : generatingQr ? (
                                    <div style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontSize: 13 }}>
                                        Đang tạo QR...
                                    </div>
                                ) : null}
                                {displayQrUrl && (
                                    <button style={{
                                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px',
                                        border: '1.5px solid #ECEEF2', borderRadius: 8, background: '#fff',
                                        fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                    }} onClick={() => {
                                        const a = document.createElement('a');
                                        a.href = displayQrUrl;
                                        a.download = `QR_${activeBanking.accountNumber}.png`;
                                        a.click();
                                    }}>
                                        <Download size={14} /> Tải QR
                                    </button>
                                )}
                            </div>
                            {/* Info */}
                            <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#1A1A1A', marginBottom: 16 }}>
                                    {activeBanking.bankName}
                                    <span style={{ fontSize: 12, fontWeight: 600, color: '#22C55E', background: '#F3FEF7', padding: '2px 10px', borderRadius: 999, marginLeft: 10 }}>Đang hoạt động</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                        <span style={{ color: '#6B7280' }}>Số tài khoản</span>
                                        <span style={{ fontWeight: 600, color: '#0098F6', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {activeBanking.accountNumber}
                                            <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', padding: 2 }}
                                                onClick={() => handleCopy(activeBanking.accountNumber, 'stk')}>
                                                <Copy size={13} />
                                            </button>
                                            {copied === 'stk' && <span style={{ fontSize: 11, color: '#22C55E' }}>Đã copy</span>}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                                        <span style={{ color: '#6B7280' }}>Chủ tài khoản</span>
                                        <span style={{ fontWeight: 600, color: '#1A1A1A' }}>{activeBanking.accountName}</span>
                                    </div>
                                </div>
                                <button style={{
                                    marginTop: 16, display: 'flex', alignItems: 'center', gap: 6,
                                    padding: '8px 16px', border: '1.5px solid #ECEEF2', borderRadius: 8,
                                    background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                                }} onClick={() => handleCopy(
                                    `Ngân hàng: ${activeBanking.bankName}\nSTK: ${activeBanking.accountNumber}\nChủ TK: ${activeBanking.accountName}`,
                                    'info',
                                )}>
                                    <Share2 size={14} /> {copied === 'info' ? 'Đã copy!' : 'Copy thông tin'}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Banking list */}
                    {loading ? (
                        <div className={styles.loading}><div className={styles.spinner} /> Đang tải...</div>
                    ) : bankingList.length === 0 ? (
                        <div className={styles.empty}>Chưa có tài khoản ngân hàng nào</div>
                    ) : (
                        <div className={styles.grid}>
                            {bankingList.map((item) => (
                                <div key={item.id}
                                    className={`${styles.card}${editId === item.id ? ` ${styles.cardActive}` : ''}`}
                                    onClick={() => openEdit(item)}>
                                    <div>
                                        <div className={styles.cardBadges} style={{ marginBottom: 6 }}>
                                            <span className={`${styles.badge} ${item.isActive === true || item.isActive === 'true' ? styles.badgeActive : styles.badgeInactive}`}>
                                                {item.isActive === true || item.isActive === 'true' ? 'Hoạt động' : 'Tạm dừng'}
                                            </span>
                                        </div>
                                        <div className={styles.cardFormula}>{item.bankName}</div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0098F6', margin: '4px 0' }}>{item.accountNumber}</div>
                                        <div className={styles.cardMeta}>{item.accountName}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* RIGHT: Panel */}
                {showPanel && (
                    <div className={styles.layoutRight}>
                        <div className={styles.panel}>
                            <div className={styles.panelHeader}>
                                <h3 className={styles.panelTitle}>
                                    {panelMode === 'create' ? 'Thêm tài khoản' : 'Chỉnh sửa'}
                                </h3>
                                <button className={styles.panelClose} onClick={closePanel}><X size={16} /></button>
                            </div>
                            <div className={styles.panelBody}>
                                {formError && <div className={styles.formError}>{formError}</div>}

                                {/* Bank select */}
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Ngân hàng <span>*</span></label>
                                    <div style={{ position: 'relative' }}>
                                        <button className={styles.formInput} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer', textAlign: 'left',
                                        }} onClick={() => setShowBankDropdown(!showBankDropdown)}>
                                            <span style={{ color: form.bankName ? '#1A1A1A' : '#B0B7C3' }}>
                                                {form.bankName || 'Chọn ngân hàng'}
                                            </span>
                                            <ChevronDown size={16} color="#6B7280" />
                                        </button>
                                        {showBankDropdown && (
                                            <div style={{
                                                position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                                                background: '#fff', border: '1.5px solid #ECEEF2', borderRadius: 10,
                                                boxShadow: '0 8px 24px rgba(0,0,0,0.08)', zIndex: 10, overflow: 'hidden',
                                            }}>
                                                <div style={{ padding: '8px 10px', borderBottom: '1px solid #ECEEF2' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', height: 34, border: '1px solid #ECEEF2', borderRadius: 8, background: '#FAFBFC' }}>
                                                        <Search size={14} color="#6B7280" />
                                                        <input style={{ flex: 1, border: 'none', background: 'none', outline: 'none', fontSize: 13, fontFamily: 'inherit' }}
                                                            value={bankSearch} onChange={(e) => setBankSearch(e.target.value)} placeholder="Tìm ngân hàng..." autoFocus />
                                                    </div>
                                                </div>
                                                {filteredBanks.map((b) => (
                                                    <button key={b.bin} style={{
                                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        width: '100%', padding: '10px 14px', border: 'none',
                                                        background: form.bankBin === b.bin ? '#E6F4FF' : 'none',
                                                        fontSize: 14, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                                                    }} onClick={() => selectBank(b)}>
                                                        {b.name}
                                                        {form.bankBin === b.bin && <Check size={16} color="#0098F6" />}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Số tài khoản <span>*</span></label>
                                    <input className={styles.formInput} value={form.accountNumber}
                                        onChange={(e) => setForm((p) => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '') }))}
                                        placeholder="Nhập số tài khoản" inputMode="numeric" />
                                </div>

                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Chủ tài khoản <span>*</span></label>
                                    <FormattedInput className={styles.formInput} value={form.accountName}
                                        format={toUpper} parse={toUpper} isValueChar={() => true}
                                        onChange={(raw) => setForm((p) => ({ ...p, accountName: raw }))}
                                        placeholder="VD: NGUYEN VAN A (in hoa, không dấu)"
                                        style={{ textTransform: 'uppercase' }} />
                                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>
                                        Viết IN HOA, không dấu
                                    </div>
                                </div>
                            </div>

                            <div className={styles.panelFooter}>
                                {panelMode === 'edit' && (
                                    <>
                                        <button className={`${styles.panelBtn} ${styles.panelBtnDanger}`}
                                            onClick={() => handleDelete(editId)}>Xoá</button>
                                        {(() => { const b = bankingList.find((x) => x.id === editId); return !(b?.isActive === true || b?.isActive === 'true'); })() && (
                                            <button className={styles.panelBtn} onClick={() => handleToggleActive(editId)}>Kích hoạt</button>
                                        )}
                                    </>
                                )}
                                <div style={{ flex: 1 }} />
                                <button className={styles.panelBtn} onClick={closePanel}>Đóng</button>
                                <button className={`${styles.panelBtn} ${styles.panelBtnPrimary}`} onClick={handleSave} disabled={saving}>
                                    {saving ? 'Đang lưu...' : panelMode === 'create' ? 'Tạo & Tạo QR' : 'Lưu'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {toastMsg && (
                <div className={styles.toastOverlay}>
                    <div className={styles.toastModal}>
                        <div className={styles.toastIcon} style={toastMsg.type === 'error' ? { background: '#EF4444' } : undefined}>
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span className={styles.toastText}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
